import { useEffect, useState, useRef } from "react";
import { userAPI, authAPI, roleAPI, officerAPI, contractOfficerAPI, lookupAPI } from "../services/api";
import { StatusBadge, Modal, Btn, Th, Td, ActionBtns, ThActions, ConfirmDialog, Toast, Can, errMessage, TableScroll, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";
import {
  MdWarning, MdSearch, MdBadge, MdPerson, MdEmail, MdPhone,
  MdAccessTime, MdCalendarToday, MdEdit, MdLock, MdCheckCircle, MdDelete,
  MdLockReset,
} from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const STATUS_COLOR = {
  ACTIVE:    "#10B981",
  INACTIVE:  "#6B7280",
  BLOCKED:   "#EF4444",
  SUSPENDED: "#F59E0B",
  LOCKED:    "#991B1B",
};

const inputStyle = {
  width:"100%", padding:"9px 12px", border:`1px solid ${BORDER}`,
  borderRadius:8, fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};
const errStyle = (key, errors) => errors?.[key]
  ? { ...inputStyle, border:"1.5px solid #EF4444", background:"#FFF5F5" }
  : inputStyle;

const toDisplay = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB"); } catch { return iso; }
};

// api.js hangs the parsed error body on `.body` (older builds JSON-stringified it
// into `.message`) — read both so field-level errors are never missed.
const errBody = (e) => {
  if (e?.body && typeof e.body === "object") return e.body;
  try { return JSON.parse(e?.message); } catch { return null; }
};

function SkeletonRow() {
  return (
    <tr>
      {Array(8).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function ErrMsg({ field, errors }) {
  if (!errors?.[field]) return null;
  return (
    <p style={{ fontSize:11, color:"#EF4444", marginTop:3, fontFamily:KH, display:"flex", alignItems:"center", gap:3 }}>
      <MdWarning size={13}/> {errors[field]}
    </p>
  );
}

function Label({ children, required }) {
  return (
    <label style={{ display:"block", fontSize:12, color:MUTED, marginBottom:4, fontFamily:KH }}>
      {children}
      {required && <span style={{ color:"#EF4444", marginLeft:3 }}>*</span>}
    </label>
  );
}

function SectionTitle({ children }) {
  return (
    <p style={{ fontSize:11, color:MUTED, fontWeight:600, margin:"16px 0 10px",
                borderBottom:`1px solid ${BORDER}`, paddingBottom:4, fontFamily:KH }}>
      {children}
    </p>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:10,
                  background:BG, padding:"9px 14px", borderRadius:8, marginBottom:6 }}>
      <span style={{ flexShrink:0, marginTop:2, color:MUTED, display:"flex", alignItems:"center" }}>{icon}</span>
      <div>
        <p style={{ fontSize:11, color:MUTED, marginBottom:2, fontFamily:KH }}>{label}</p>
        <div style={{ fontSize:13, fontWeight:500, color:"var(--text)", fontFamily:KH }}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

export default function Users() {
  const { t } = useLanguage();
  const [list,         setList]         = useState([]);
  const [roles,        setRoles]        = useState([]);
  const [officers,     setOfficers]     = useState([]);
  const [contractOfficers, setContractOfficers] = useState([]);
  const [userStatuses, setUserStatuses] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [roleFil,      setRoleFil]      = useState("");
  const [statusFil,    setStatusFil]    = useState("");
  const [modal,        setModal]        = useState(null);
  const [form,         setForm]         = useState({});
  const [selected,     setSelected]     = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [editId,       setEditId]       = useState(null);
  const [errors,       setErrors]       = useState({});
  const [deleteId,     setDeleteId]     = useState(null);
  const [toast,        setToast]        = useState(null);
  const [err,          setErr]          = useState(null);
  const [page,         setPage]         = useState(0);
  const [pageSize,     setPageSize]     = useState(25);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalItems,   setTotalItems]   = useState(0);
  const [resetId,      setResetId]      = useState(null);
  const [tempPassword, setTempPassword] = useState(null);

  const roleRef    = useRef(null);
  const nameKhRef  = useRef(null);
  const emailRef   = useRef(null);
  const statusRef  = useRef(null);

  const getStatusLabel = (code) =>
    userStatuses.find(s => s.statusCode === code)?.labelKh || code || "—";
  const getStatusColor = (code) => STATUS_COLOR[code] || "#94A3B8";

  const load = async () => {
    setLoading(true);
    try {
      const res  = await userAPI.getAll(
        page, pageSize,
        roleFil   ? parseInt(roleFil)   : undefined,
        statusFil || undefined,
        search    || undefined
      );
      const data = res?.data?.data ?? res?.data;
      setList(data?.content || []);
      setTotalPages(data?.totalPages    || 1);
      setTotalItems(data?.totalElements || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    lookupAPI.getUserStatuses().then(r => {
      const d = r?.data?.data ?? r?.data;
      setUserStatuses(Array.isArray(d) ? d : []);
    }).catch(() => {});

    roleAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      setRoles((Array.isArray(d) ? d : []).map(x => ({
        ...x, id: x.roleId ?? x.id, displayName: x.displayName ?? x.roleName,
      })));
    }).catch(() => {});

    officerAPI.getAll(0, 200).then(r => {
      const d = r?.data?.data ?? r?.data;
      setOfficers((d?.content || []).map(o => ({
        ...o, id: o.officerId ?? o.id,
      })));
    }).catch(() => {});

    contractOfficerAPI.getAll(0, 200).then(r => {
      const d = r?.data?.data ?? r?.data;
      setContractOfficers((d?.content || []).map(o => ({
        ...o, id: o.contractOfficerId ?? o.id,
      })));
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [page, pageSize, roleFil, statusFil]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    const reqFields = [
      { key:"roleId",     msg:t("សូមជ្រើសរើសតួនាទី"),   ref:roleRef,   empty:!form.roleId },
      { key:"userNameKh", msg:t("សូមបញ្ចូលឈ្មោះខ្មែរ"),  ref:nameKhRef, empty:!form.userNameKh?.trim() },
      { key:"email",      msg:t("សូមបញ្ចូលអ៊ីមែល"),       ref:emailRef,  empty:!form.email?.trim() },
      { key:"statusCode", msg:t("សូមជ្រើសរើសសស្ថានភាព"),  ref:statusRef, empty:!form.statusCode },
    ];
    const reqErrors = {};
    reqFields.forEach(f => { if (f.empty) reqErrors[f.key] = f.msg; });
    if (Object.keys(reqErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...reqErrors }));
      const first = reqFields.find(f => f.empty && f.ref?.current);
      if (first) setTimeout(() => first.ref.current.focus(), 80);
      return;
    }

    setSaving(true);
    try {
      const userType = form.userType || "OFFICER";
      const payload = {
        userType,
        roleId:     form.roleId     ? parseInt(form.roleId, 10)     : null,
        officerId:         userType === "OFFICER"          && form.officerId          ? parseInt(form.officerId, 10)          : null,
        contractOfficerId: userType === "CONTRACT_OFFICER" && form.contractOfficerId  ? parseInt(form.contractOfficerId, 10)  : null,
        userNameKh: form.userNameKh  || "",
        userNameEn: form.userNameEn  || "",
        email:      form.email       || "",
        phone:      form.phone       || "",
        statusCode: form.statusCode  || "ACTIVE",
      };

      if (editId) {
        await userAPI.update(editId, payload);
      } else {
        await userAPI.create(payload);
      }

      await load();
      setToast(editId ? "កែប្រែអ្នកប្រើប្រាស់បានជោគជ័យ!" : "បន្ថែមអ្នកប្រើប្រាស់បានជោគជ័យ!");
      setModal(null); setForm({}); setEditId(null); setErrors({});
    } catch (e) {
      const b = errBody(e);
      if (b?.data && typeof b.data === "object") {
        setErrors(b.data);
        return;
      }
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await userAPI.delete(id);
      await load();
      if ((selected?.userId ?? selected?.id) === id) setModal(null);
      setToast(t("លុបអ្នកប្រើប្រាស់បានជោគជ័យ!"));
    } catch (e) { setErr(errMessage(e)); }
  };

  const doResetPassword = async () => {
    const id = resetId;
    setResetId(null);
    try {
      const res  = await authAPI.resetPassword(id);
      const data = res?.data ?? res;
      // backend returns a random one-time password; this is the only time it is shown
      setTempPassword(data?.temporaryPassword || "");
    } catch (e) { setErr(errMessage(e)); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await userAPI.updateStatus(id, { statusCode: newStatus });
      await load();
    } catch (e) { setErr(errMessage(e)); }
  };

  const fillForm = (u) => {
    const info     = u.officerInfo || null;
    const userType = u.userType || info?.type
                     || (u.contractOfficerId ?? u.contractOfficer?.contractOfficerId ? "CONTRACT_OFFICER" : "OFFICER");
    const linkedId = info?.id
                     ?? (userType === "CONTRACT_OFFICER"
                          ? (u.contractOfficerId ?? u.contractOfficer?.contractOfficerId)
                          : (u.officerId ?? u.officer?.officerId));
    setForm({
      userType,
      roleId:     (u.roleId     ?? u.role?.roleId)     ? String(u.roleId ?? u.role?.roleId)         : "",
      officerId:         userType === "OFFICER"          && linkedId != null ? String(linkedId) : "",
      contractOfficerId: userType === "CONTRACT_OFFICER" && linkedId != null ? String(linkedId) : "",
      userNameKh: u.userNameKh  || "",
      userNameEn: u.userNameEn  || "",
      email:      u.email       || "",
      phone:      u.phone       || "",
      statusCode: u.statusCode  || u.status || "ACTIVE",
    });
  };

  const openEdit = async (u) => {
    const id = u.userId ?? u.id;
    setEditId(id);
    setErrors({});
    setForm({});
    fillForm(u);
    setModal("add");

    try {
      const res  = await userAPI.getById(id);
      const full = res?.data?.data ?? res?.data;
      if (full && typeof full === "object") fillForm({ ...u, ...full });
    } catch (e) { console.error(e); }
  };

  const filtered = list.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [u.userNameKh, u.userNameEn, u.email, u.phone, u.uuid]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  return (
    <div>
      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center", flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:200, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`, borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key==="Enter" && load()}
            placeholder={t("ស្វែងរក ឈ្មោះ អ៊ីមែល...")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>

        <select value={roleFil} onChange={e => { setRoleFil(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("តួនាទីទាំងអស់")}</option>
          {roles.map(r => (
            <option key={r.id} value={String(r.id)}>{r.displayName || r.roleName}</option>
          ))}
        </select>

        <select value={statusFil} onChange={e => { setStatusFil(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          {userStatuses.map(s => (
            <option key={s.statusCode} value={s.statusCode}>
              {s.labelKh || s.statusCode}
            </option>
          ))}
        </select>

        <Can perm="user" action="create">
        <button onClick={() => { setEditId(null); setErrors({}); setForm({ statusCode:"ACTIVE", userType:"OFFICER" }); setModal("add"); }}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff", border:"none",
                   borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:500,
                   whiteSpace:"nowrap", fontFamily:KH }}>
          {t("+ បន្ថែមអ្នកប្រើប្រាស់")}
        </button>
        </Can>
      </div>

      {!loading && (
        <p style={{ fontSize:12, color:MUTED, marginBottom:10, fontFamily:KH }}>
          {t("សរុប:")} <strong style={{ color:BROWN }}>{totalItems}</strong> {t("នាក់")}
        </p>
      )}

      <div style={{ background:"var(--surface)", borderRadius:10, border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("ឈ្មោះខ្មែរ")}</Th>
              <Th>{t("ឈ្មោះអង់គ្លេស")}</Th>
              <Th>{t("អ៊ីមែល")}</Th>
              <Th>{t("ទូរស័ព្ទ")}</Th>
              <Th>{t("តួនាទី")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <Th>{t("ចូលចុងក្រោយ")}</Th>
              <ThActions perm="user" />
            </tr>
          </thead>
          <tbody>
            {loading ? [1,2,3,4].map(i => <SkeletonRow key={i} />)
            : filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ padding:"32px", textAlign:"center",
                                            color:MUTED, fontSize:13, fontFamily:KH }}>
                {t("គ្មានអ្នកប្រើប្រាស់")}
              </td></tr>
            ) : filtered.map(u => {
              const uid  = u.userId ?? u.id;
              const sc   = getStatusColor(u.statusCode || u.status);
              const sl   = getStatusLabel(u.statusCode || u.status);
              return (
                <tr key={uid}
                  onMouseEnter={e => e.currentTarget.style.background=BG}
                  onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                  <Td>{u.userNameKh || "—"}</Td>
                  <Td muted>{u.userNameEn || "—"}</Td>
                  <Td muted>{u.email || "—"}</Td>
                  <Td muted>{u.phone || "—"}</Td>
                  <Td>
                    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11,
                                   fontWeight:600, background:"#EDE9FE", color:"#5B21B6",
                                   fontFamily:KH }}>
                      {u.roleName || u.role?.displayName || u.role?.roleName || "—"}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11,
                                   fontWeight:600, background:sc+"22", color:sc, fontFamily:KH }}>
                      {sl}
                    </span>
                  </Td>
                  <Td muted>{toDisplay(u.lastLoginAt)}</Td>
                  <ActionBtns perm="user"
                    onView={() => { setSelected(u); setModal("view"); }}
                    onEdit={() => openEdit(u)}
                    onDelete={() => handleDelete(uid)}
                  />
                </tr>
              );
            })}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបអ្នកប្រើប្រាស់នេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <ConfirmDialog
        open={resetId !== null}
        title="កំណត់ពាក្យសម្ងាត់ឡើងវិញ"
        message={t("តើអ្នកពិតជាចង់កំណត់ពាក្យសម្ងាត់អ្នកប្រើប្រាស់នេះឡើងវិញជាលំនាំដើម ជាពាក្យសម្ងាត់បណ្តោះអាសន្នមែនទេ? អ្នកប្រើនឹងត្រូវប្តូរពាក្យសម្ងាត់ថ្មីនៅពេលចូលលើកក្រោយ។")}
        confirmLabel="កំណត់ឡើងវិញ"
        confirmColor={BROWN}
        icon={<MdLockReset size={22} color={BROWN}/>}
        iconBg="#FEF3C7"
        warning=""
        onConfirm={doResetPassword}
        onCancel={() => setResetId(null)}
      />
      {tempPassword !== null && (
        <Modal title="ពាក្យសម្ងាត់បណ្តោះអាសន្ន" width={420} onClose={() => setTempPassword(null)}>
          <p style={{ fontSize:13, color:MUTED, fontFamily:KH, marginBottom:12 }}>
            {t("សូមផ្តល់ពាក្យសម្ងាត់នេះទៅអ្នកប្រើ។ វានឹងបង្ហាញតែម្តងគត់ ហើយអ្នកប្រើត្រូវប្តូរវានៅពេលចូលលើកក្រោយ។")}
          </p>
          <div style={{ padding:"12px 14px", background:BG, border:`1px solid ${BORDER}`, borderRadius:8,
                        fontFamily:"monospace", fontSize:18, letterSpacing:1, textAlign:"center",
                        userSelect:"all", color:"var(--text)" }}>
            {tempPassword || "—"}
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", marginTop:20 }}>
            <Btn onClick={() => setTempPassword(null)}>{t("បិទ")}</Btn>
          </div>
        </Modal>
      )}
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "add" && (
        <Modal key={editId||"new"}
          title={editId ? "កែប្រែអ្នកប្រើប្រាស់" : "បន្ថែមអ្នកប្រើប្រាស់"}
          onClose={() => { setModal(null); setForm({}); setEditId(null); setErrors({}); }}>

          <SectionTitle>{t("ព័ត៌មានគណនី")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <div style={{ marginBottom:14 }}>
              <Label required>{t("តួនាទី")}</Label>
              <select ref={roleRef} value={form.roleId||""}
                onChange={e => { f("roleId",e.target.value); setErrors(p=>({...p,roleId:undefined})); }}
                style={errStyle("roleId", errors)}>
                <option value="">{t("ជ្រើសតួនាទី...")}</option>
                {roles.map(r => (
                  <option key={r.id} value={String(r.id)}>{r.displayName||r.roleName}</option>
                ))}
              </select>
              <ErrMsg field="roleId" errors={errors} />
            </div>

            <div style={{ marginBottom:14 }}>
              <Label required>{t("ប្រភេទអ្នកប្រើ")}</Label>
              <select value={form.userType||"OFFICER"}
                onChange={e => {
                  setForm(prev => ({ ...prev, userType:e.target.value, officerId:"", contractOfficerId:"" }));
                }}
                style={inputStyle}>
                <option value="OFFICER">{t("មន្ត្រី (OFFICER)")}</option>
                <option value="CONTRACT_OFFICER">{t("មន្ត្រីកិច្ចសន្យា (CONTRACT_OFFICER)")}</option>
              </select>
            </div>

            {(form.userType||"OFFICER") === "OFFICER" ? (
              <div style={{ marginBottom:14 }}>
                <Label>{t("ភ្ជាប់មន្ត្រី")}</Label>
                <select value={form.officerId||""}
                  onChange={e => {
                    const val = e.target.value;
                    if (val) {
                      const o = officers.find(o => String(o.id) === val);
                      if (o) {
                        setForm(prev => ({
                          ...prev,
                          officerId:  val,
                          userNameKh: o.fullNameKh  || prev.userNameKh || "",
                          userNameEn: o.fullNameEn  || prev.userNameEn || "",
                          email:      o.email       || prev.email      || "",
                          phone:      o.phone       || prev.phone      || "",
                        }));
                        setErrors(prev => ({ ...prev, userNameKh:undefined, email:undefined }));
                        return;
                      }
                    }
                    f("officerId", val);
                  }}
                  style={inputStyle}>
                  <option value="">{t("ជ្រើសរើសមន្ត្រី...")}</option>
                  {officers.map(o => (
                    <option key={o.id} value={String(o.id)}>
                      {o.fullNameKh || o.fullNameEn || `ID: ${o.id}`}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div style={{ marginBottom:14 }}>
                <Label>{t("ភ្ជាប់មន្ត្រីកិច្ចសន្យា")}</Label>
                <select value={form.contractOfficerId||""}
                  onChange={e => {
                    const val = e.target.value;
                    if (val) {
                      const o = contractOfficers.find(o => String(o.id) === val);
                      if (o) {
                        setForm(prev => ({
                          ...prev,
                          contractOfficerId: val,
                          userNameKh: o.fullNameKh  || prev.userNameKh || "",
                          userNameEn: o.fullNameEn  || prev.userNameEn || "",
                          email:      o.email       || prev.email      || "",
                          phone:      o.phone       || prev.phone      || "",
                        }));
                        setErrors(prev => ({ ...prev, userNameKh:undefined, email:undefined }));
                        return;
                      }
                    }
                    f("contractOfficerId", val);
                  }}
                  style={inputStyle}>
                  <option value="">{t("ជ្រើសរើសមន្ត្រី...")}</option>
                  {contractOfficers.map(o => (
                    <option key={o.id} value={String(o.id)}>
                      {o.fullNameKh || o.fullNameEn || `ID: ${o.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginBottom:14 }}>
              <Label required>{t("ឈ្មោះខ្មែរ")}</Label>
              <input ref={nameKhRef} value={form.userNameKh||""}
                onChange={e => { f("userNameKh",e.target.value); setErrors(p=>({...p,userNameKh:undefined})); }}
                placeholder={t("ឧ. ចាន់ ដារ៉ា")}
                style={errStyle("userNameKh", errors)} />
              <ErrMsg field="userNameKh" errors={errors} />
            </div>

            <div style={{ marginBottom:14 }}>
              <Label>{t("ឈ្មោះអង់គ្លេស")}</Label>
              <input value={form.userNameEn||""}
                onChange={e => f("userNameEn",e.target.value)}
                placeholder="e.g. Chan Dara" style={inputStyle} />
            </div>

            <div style={{ marginBottom:14 }}>
              <Label required>{t("អ៊ីមែល")}</Label>
              <input ref={emailRef} type="email" value={form.email||""}
                onChange={e => { f("email",e.target.value); setErrors(p=>({...p,email:undefined})); }}
                placeholder="example@gmail.com"
                style={errStyle("email", errors)} />
              <ErrMsg field="email" errors={errors} />
            </div>

            <div style={{ marginBottom:14 }}>
              <Label>{t("ទូរស័ព្ទ")}</Label>
              <input value={form.phone||""}
                onChange={e => f("phone",e.target.value)}
                placeholder="0xx xxx xxx" style={inputStyle} />
            </div>

            <div style={{ marginBottom:14 }}>
              <Label required>{t("ស្ថានភាព")}</Label>
              <select ref={statusRef} value={form.statusCode||"ACTIVE"}
                onChange={e => { f("statusCode",e.target.value); setErrors(p=>({...p,statusCode:undefined})); }}
                style={errStyle("statusCode", errors)}>
                <option value="">{t("ជ្រើសសស្ថានភាព...")}</option>
                {userStatuses.length > 0
                  ? userStatuses.map(s => (
                      <option key={s.statusCode} value={s.statusCode}>
                        {s.labelKh || s.statusCode}
                      </option>
                    ))
                  : ["ACTIVE","INACTIVE","BLOCKED"].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))
                }
              </select>
              <ErrMsg field="statusCode" errors={errors} />
            </div>
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setEditId(null); setErrors({}); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave} style={{ opacity:saving?0.7:1 }}>
              {saving ? "កំពុងរក្សាទុក..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal
          title={selected.userNameKh || selected.userNameEn || "អ្នកប្រើប្រាស់"}
          subtitle={selected.userNameEn || ""}
          onClose={() => setModal(null)}>

          <div style={{ display:"flex", justifyContent:"center", marginBottom:14 }}>
            <div style={{ width:96, height:96, borderRadius:"50%", overflow:"hidden",
                          border:`2px solid ${BORDER}`, background:BG,
                          display:"flex", alignItems:"center", justifyContent:"center" }}>
              <MdPerson size={44} color={MUTED} />
            </div>
          </div>

          <div style={{ marginBottom:12, display:"flex", gap:8 }}>
            <span style={{ padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:600,
                           background:getAnnColor(selected.statusCode||selected.status)+"22",
                           color:getStatusColor(selected.statusCode||selected.status),
                           fontFamily:KH }}>
              {getStatusLabel(selected.statusCode || selected.status)}
            </span>
            <span style={{ padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:600,
                           background:"#EDE9FE", color:"#5B21B6", fontFamily:KH }}>
              {selected.roleName || selected.role?.displayName || "—"}
            </span>
          </div>

          <SectionTitle>{t("ព័ត៌មានអ្នកប្រើ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdBadge size={16}/>}  label={t("លេខសម្គាល់")}      value={selected.userId ?? selected.id} />
            <InfoRow icon={<MdPerson size={16}/>} label={t("ឈ្មោះខ្មែរ")}  value={selected.userNameKh} />
            <InfoRow icon={<MdPerson size={16}/>} label={t("ឈ្មោះអង់គ្លេស")}  value={selected.userNameEn} />
          </div>

          <SectionTitle>{t("ព័ត៌មានទំនាក់ទំនង")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdEmail size={16}/>} label={t("អ៊ីមែល")}  value={selected.email} />
            <InfoRow icon={<MdPhone size={16}/>} label={t("ទូរស័ព្ទ")}  value={selected.phone} />
          </div>

          <SectionTitle>{t("ព័ត៌មានប្រព័ន្ធ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdBadge size={16}/>}        label={t("តួនាទី")}        value={selected.roleName || selected.role?.displayName} />
            <InfoRow icon={<MdAccessTime size={16}/>}   label={t("ការចូលចុងក្រោយ")} value={toDisplay(selected.lastLoginAt)} />
            <InfoRow icon={<MdCalendarToday size={16}/>} label={t("បង្កើតនៅ")}      value={toDisplay(selected.createdAt)} />
            <InfoRow icon={<MdEdit size={16}/>}         label={t("កែប្រែនៅ")}       value={toDisplay(selected.updatedAt)} />
          </div>

          <div style={{ marginTop:20, display:"flex", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
            <div style={{ display:"flex", gap:8 }}>
              <Can perm="user" action="edit">
              <Btn variant="outline" onClick={() => openEdit(selected)} style={{display:"flex",alignItems:"center",gap:5}}>
                <MdEdit size={14}/> {t("កែប្រែ")}
              </Btn>
              </Can>
              <Can perm="user" action="edit">
              <Btn variant="outline"
                onClick={() => setResetId(selected.userId ?? selected.id)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdLockReset size={14}/> {t("កំណត់ពាក្យសម្ងាត់ឡើងវិញ")}
              </Btn>
              </Can>
              <Can perm="user" action="edit">
              {(selected.statusCode||selected.status) === "ACTIVE"
                ? <Btn variant="outline"
                    onClick={() => { handleStatusChange(selected.userId??selected.id,"BLOCKED"); setModal(null); }}
                    style={{display:"flex",alignItems:"center",gap:5}}>
                    <MdLock size={14}/> {t("បិទ")}
                  </Btn>
                : <Btn variant="outline"
                    onClick={() => { handleStatusChange(selected.userId??selected.id,"ACTIVE"); setModal(null); }}
                    style={{display:"flex",alignItems:"center",gap:5}}>
                    <MdCheckCircle size={14}/> {t("ធ្វើឱ្យសកម្ម")}
                  </Btn>
              }
              </Can>
            </div>
            <Can perm="user" action="delete">
            <Btn variant="danger" onClick={() => handleDelete(selected.userId??selected.id)}
              style={{display:"flex",alignItems:"center",gap:5}}>
              <MdDelete size={14}/> {t("លុប")}
            </Btn>
            </Can>
          </div>
        </Modal>
      )}
    </div>
  );
}

function getAnnColor(code) {
  const colors = {
    ACTIVE:"#10B981", INACTIVE:"#6B7280", BLOCKED:"#EF4444",
    SUSPENDED:"#F59E0B", LOCKED:"#991B1B",
  };
  return colors[code] || "#94A3B8";
}